import React, { FC, useRef } from 'react';
import { Icon } from './icons/Icon';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';

export interface IllustrationComment {
    id: string;
    position: { x: number; y: number };
    content: string;
    createdAt: number;
    user: { id: string; displayName: string; iconUrl: string | null };
}

interface IllustrationViewerProps {
    imageUrl: string;
    comments?: IllustrationComment[];
    onCommentClick?: (comment: IllustrationComment) => void;
    onAddComment?: (x: number, y: number) => void;
    isLoggedIn?: boolean;
}

export const IllustrationViewer: FC<IllustrationViewerProps> = ({
    imageUrl,
    comments = [],
    onCommentClick,
    onAddComment,
    isLoggedIn = false
}) => {
    const { playSound } = useGamenics();
    const containerRef = useRef<HTMLDivElement>(null);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isLoggedIn || !onAddComment || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        playSound(GamenicsEvent.TAP_CONFIRM);
        onAddComment(x, y);
    };

    return (
        <div 
            className="w-full relative bg-cream rounded-xl overflow-hidden border-2 border-ink/15 group select-none flex items-center justify-center min-h-[300px]"
            ref={containerRef}
        >
            <img 
                src={imageUrl} 
                alt="Illustration" 
                className={`w-full h-auto object-contain max-h-[70vh] ${isLoggedIn ? 'cursor-crosshair' : ''}`}
                onClick={handleClick}
            />
            {comments.map(c => (
                <div 
                    key={c.id}
                    className="absolute w-8 h-8 -ml-4 -mt-4 rounded-full border-2 border-pop  cursor-pointer hover:scale-125 hover:z-10 transition-all bg-cream-2 overflow-hidden"
                    style={{ left: `${c.position.x}%`, top: `${c.position.y}%` }}
                    onClick={(e) => {
                        e.stopPropagation();
                        playSound(GamenicsEvent.TAP_CONFIRM);
                        onCommentClick?.(c);
                    }}
                    title={c.content}
                >
                    {c.user.iconUrl ? <img src={c.user.iconUrl} alt={c.user.displayName} /> : <div className="text-[10px] flex items-center justify-center w-full h-full text-ink bg-cream-2"><Icon name="user" size={10} className="text-ink-3" /></div>}
                </div>
            ))}
            {isLoggedIn && onAddComment && (
                <div className="absolute bottom-4 left-0 w-full text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <span className="bg-ink/70 text-ink text-xs px-3 py-1.5 rounded-full border-2 border-ink shadow-lg">
                        絵の好きな箇所をクリックしてピンポイント称賛
                    </span>
                </div>
            )}
        </div>
    );
};
